import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaExternalLinkAlt,
  FaMoneyBillWave,
  FaPlus,
  FaReceipt,
  FaRobot,
  FaSync,
} from 'react-icons/fa';
import RefundForm from './RefundForm';
import {
  GRANTED_STATUSES,
  formatRefundMoney,
  newRefundForm,
  refundAmountPending,
  refundPayload,
} from '../utils/refunds';
import './Refunds.css';

// The ticket's Refunds tab (MessageWindow side panel). The ticket context
// (order, client, declared values) comes from the refunds ticket lookup, so
// the form offers the same prefill as the refunds page (KI-158).
const TicketRefunds = ({ ticketId, ticket }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const navigate = useNavigate();
  const [refunds, setRefunds] = useState([]);
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(() => newRefundForm(null));

  const grantedTotal = useMemo(
    () => refunds
      .filter((refund) => GRANTED_STATUSES.includes(refund.status))
      .reduce((sum, refund) => sum + Number(refund.total_refund_amount || 0), 0),
    [refunds]
  );

  const token = localStorage.getItem('access_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchRefunds = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/api/logistics/tickets/${ticketId}/refunds`, { headers });
      setRefunds(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch ticket refunds:', err);
      setError(t('refunds.fetchError', 'Failed to load refunds.'));
    } finally {
      setLoading(false);
    }
  }, [headers, t, ticketId]);

  useEffect(() => {
    fetchRefunds();
  }, [fetchRefunds]);

  useEffect(() => {
    if (!ticketId) return undefined;
    let cancelled = false;
    axios.get('/api/logistics/refunds/ticket-lookup', { headers, params: { q: ticketId } })
      .then((response) => {
        const item = response.data?.items?.[0] || null;
        if (cancelled) return;
        setContext(item);
        setForm(newRefundForm(item));
      })
      .catch((err) => console.error('Failed to load the ticket refund context:', err));
    return () => { cancelled = true; };
  }, [headers, ticketId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(`/api/logistics/tickets/${ticketId}/refunds`, refundPayload(form), { headers });
      setForm(newRefundForm(context));
      setSuccess(t('refunds.createSuccess', 'Refund created successfully.'));
      await fetchRefunds();
    } catch (err) {
      console.error('Failed to create refund:', err);
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('refunds.createError', 'Failed to create refund.'));
    } finally {
      setSaving(false);
    }
  };

  const orderReference = context?.order_reference;

  return (
    <div className="ticket-refunds">
      <div className="ticket-refunds-header">
        <div>
          <span className="ticket-refunds-kicker">{t('refunds.ticketTabKicker', 'Refunds')}</span>
          <h3>{t('refunds.ticketTabTitle', 'Ticket refunds')}</h3>
        </div>
        <button type="button" className="refund-icon-button" onClick={fetchRefunds} disabled={loading}
          aria-label={t('refunds.refresh', 'Refresh')}>
          <FaSync />
        </button>
      </div>

      <div className="ticket-refunds-summary">
        <div>
          <span>{t('refunds.totalCreated', 'Created')}</span>
          <strong>{refunds.length}</strong>
        </div>
        <div>
          <span>{t('refunds.grantedAmount', 'Granted')}</span>
          <strong>{formatRefundMoney(grantedTotal, refunds[0]?.currency || form.currency, lang)}</strong>
        </div>
      </div>

      {(ticket?.external_ticket_id || orderReference) && (
        <div className="ticket-refunds-context">
          <FaReceipt />
          <span>
            {[ticket?.external_ticket_id, orderReference && t('refunds.orderShort', 'Cde {{order}}', { order: orderReference }),
              context?.client_name].filter(Boolean).join(' · ')}
          </span>
        </div>
      )}

      <div className="ticket-refunds-list">
        <div className="ticket-refunds-list-header">
          <FaMoneyBillWave />
          <span>{t('refunds.existingRefunds', 'Existing refunds')}</span>
        </div>
        {loading ? (
          <div className="refund-loading">{t('refunds.loading', 'Loading refunds...')}</div>
        ) : refunds.length === 0 ? (
          <div className="refund-empty">{t('refunds.emptyTicket', 'No refunds created for this ticket yet.')}</div>
        ) : (
          refunds.map((refund) => (
            <button
              type="button"
              key={refund._id}
              className="ticket-refund-card"
              onClick={() => navigate(`/refunds/${refund._id}`)}
            >
              <div>
                <strong>
                  {refundAmountPending(refund)
                    ? t('refunds.amountToConfirm', 'Amount to confirm')
                    : formatRefundMoney(refund.total_refund_amount, refund.currency, lang)}
                </strong>
                <span>
                  {[refund.carrier_name, t(`refunds.reasons.${refund.reason}`, refund.reason)].filter(Boolean).join(' · ')}
                </span>
              </div>
              <div>
                {refund.created_by_kind === 'ai' && (
                  <span className="rf-ai-badge" title={t('refunds.recordedByAiTitle', 'Recorded by the AI agent from the carrier\'s answer')}>
                    <FaRobot aria-hidden="true" /> {t('refunds.recordedByAi', 'AI')}
                  </span>
                )}
                <span className={`refund-status-badge ${refund.status}`}>
                  {t(`refunds.statuses.${refund.status}`, refund.status)}
                </span>
                <FaExternalLinkAlt />
              </div>
            </button>
          ))
        )}
      </div>

      <form className="refund-form compact" onSubmit={handleSubmit}>
        <RefundForm
          form={form}
          onChange={(field, value) => setForm((prev) => ({ ...prev, [field]: value }))}
          declared={context?.declared}
          ticketOrder={orderReference}
          idPrefix="rfm-ticket"
        />

        {error && <div className="refund-alert error">{error}</div>}
        {success && <div className="refund-alert success">{success}</div>}

        <button type="submit" className="refund-primary-button" disabled={saving}>
          <FaPlus />
          {saving ? t('refunds.saving', 'Saving...') : t('refunds.createRefund', 'Create refund')}
        </button>
      </form>
    </div>
  );
};

export default TicketRefunds;
